import { Mutation } from '@varo/graphql-schema-types';
import { useEffect, useRef, useState } from 'react';
import { SegmentInterface } from 'services/analytics/segment/types';
import PinwheelLinkEvents from 'services/graphql/mutations/PinWheelLinkEvents';
import { pinwheelEventNames } from 'shared/providers/pinwheel/helpers/pinwheelEventNameMaps';
import {
  asPinwheelBillSwitchSuccessPayload,
  asPinwheelErrorPayload,
  asPinwheelExternalAccountConnectedPayload,
  asPinwheelLoginSuccessPayload,
} from 'shared/providers/pinwheel/helpers/pinwheelEventPayload.helpers';
import { PinwheelEvents } from 'shared/providers/pinwheel/helpers/PinwheelEvents';
import {
  PinwheelLinkSessionState,
  PinwheelLinkSessionStateInterface,
} from 'shared/providers/pinwheel/helpers/PinwheelLinkSessionState';
import { pinwheelScreenNameMap } from 'shared/providers/pinwheel/helpers/pinwheelScreenNameMaps';
import { PinwheelScreens } from 'shared/providers/pinwheel/helpers/PinwheelScreens';
import { Maybe } from 'shared/utils/types';
import { useMutation } from 'urql';

export interface PinwheelEventsHandlingProps {
  onOpen: () => void;
  onOutcome: (outcome: PinwheelLinkSessionStateInterface) => void;
  linkTokenId: string;
  category: string;
  sourceName?: string;
  /**
   * specify a analytics mapping from PinwheelEvents to your desired
   * analytics string for the event.
   */
  eventNameTable?: Partial<Record<PinwheelEvents, string>>;
  segmentClient: SegmentInterface;
}

export const usePinwheelEventsHandling = ({
  onOpen,
  onOutcome,
  linkTokenId,
  segmentClient,
  category,
  sourceName,
  eventNameTable = pinwheelEventNames,
}: PinwheelEventsHandlingProps) => {
  const [isVisible, setIsVisible] = useState(false);
  // all session state should be stored in here in real time. this should
  // be reported at the end of the session to outcome callbacks.
  const linkSessionState = useRef<PinwheelLinkSessionState | null>(null);

  useEffect(() => {
    if (linkTokenId.length) {
      linkSessionState.current = new PinwheelLinkSessionState({
        linkTokenId,
      });
    }
  }, [linkTokenId]);

  useEffect(() => {
    setIsVisible(!!linkTokenId);
  }, [linkTokenId]);

  const [, executeMutation] = useMutation<Mutation>(PinwheelLinkEvents);

  const logPinwheelEvent = (
    event: string,
    eventProperties?: Record<string, unknown>,
  ) => {
    void segmentClient.logEvent(event, {
      ...(eventProperties ?? {}),
    });
  };

  const logPinwheelScreenEvent = (
    screenName: string,
    eventProperties?: Record<string, unknown>,
  ) => {
    void segmentClient.logScreenEvent(screenName, {
      ...eventProperties,
    });
  };

  const onEvent = (eventName: string, payload: Record<string, unknown>) => {
    // need to cast this since the enum is not keyed by the actual event names
    const pinwheelEvent = eventName.toLowerCase() as PinwheelEvents;

    void executeMutation({
      eventName,
      linkTokenId,
      eventPayload: JSON.stringify(payload),
    });

    let segmentEventName: Maybe<string> = null;
    let eventProperties: Maybe<Record<string, unknown>> = null;

    if (pinwheelEvent === PinwheelEvents.Open) {
      onOpen();
    }

    switch (pinwheelEvent) {
      case PinwheelEvents.MerchantLoginSuccess: {
        segmentEventName = eventNameTable[PinwheelEvents.MerchantLoginSuccess];

        const merchantLoginPayload = asPinwheelLoginSuccessPayload(payload);
        if (merchantLoginPayload) {
          eventProperties = {
            platform_id: merchantLoginPayload.platformId,
            account_id: merchantLoginPayload.accountId,
            category,
            source_name: sourceName,
          };
        }
        break;
      }

      case PinwheelEvents.ExternalAccountConnected: {
        segmentEventName =
          eventNameTable[PinwheelEvents.ExternalAccountConnected];
        const externalAccountConnectedPayload =
          asPinwheelExternalAccountConnectedPayload(payload);
        if (externalAccountConnectedPayload) {
          eventProperties = {
            institution_name: externalAccountConnectedPayload.institutionName,
            account_name: externalAccountConnectedPayload.accountName,
            category,
            source_name: sourceName,
          };
        }
        break;
      }

      case PinwheelEvents.BillSwitchSuccess: {
        segmentEventName = eventNameTable[PinwheelEvents.BillSwitchSuccess];
        const billSwitchPayload = asPinwheelBillSwitchSuccessPayload(payload);

        if (billSwitchPayload) {
          // add switched bill to session state
          linkSessionState.current?.addBillSwitch(billSwitchPayload);

          eventProperties = {
            platform_id: billSwitchPayload.platformId,
            platform_name: billSwitchPayload.platformName,
            is_integrated_switch: billSwitchPayload.isIntegratedSwitch,
            frequency: billSwitchPayload.frequency,
            next_payment_date: billSwitchPayload.nextPaymentDate,
            amount_cents: billSwitchPayload.amountCents,
            category,
            source_name: sourceName,
          };
        }
        break;
      }

      case PinwheelEvents.BillCancelSuccess: {
        segmentEventName = eventNameTable[PinwheelEvents.BillCancelSuccess];
        eventProperties = {
          category,
          source_name: sourceName,
        };
        break;
      }

      case PinwheelEvents.ScreenTransition: {
        const screenName = payload.screenName;
        if (screenName && typeof screenName === 'string') {
          const key =
            PinwheelScreens[screenName as keyof typeof PinwheelScreens];

          if (key) {
            const screenEventName = pinwheelScreenNameMap[key];
            // properties on this event are optional, so we filter out falsey
            // values.
            const eventProperties = Object.fromEntries(
              Object.entries({
                selected_employer_id: payload.selectedEmployerId,
                selected_employer_name: payload.selectedEmployerName,
                selected_platform_id: payload.selectedPlatformId,
                selected_platform_name: payload.selectedPlatformName,
                category,
                source_name: sourceName,
              }).filter(([_, value]) => Boolean(value)),
            );

            logPinwheelScreenEvent(screenEventName, eventProperties);
          }
        }
        break;
      }

      case PinwheelEvents.PinWheelError: {
        segmentEventName = eventNameTable[PinwheelEvents.PinWheelError];
        const errorPayload = asPinwheelErrorPayload(payload);
        if (errorPayload) {
          // add error to session state
          linkSessionState.current?.setPinwheelError(errorPayload);

          eventProperties = {
            source_name: sourceName,
            category,
            error_name: errorPayload.code,
            error_type: errorPayload.type,
            error_message: errorPayload.message,
            error_pending_retry: errorPayload.pendingRetry,
          };
        }
        break;
      }
    }

    if (segmentEventName) {
      logPinwheelEvent(segmentEventName, eventProperties ?? undefined);
    }
  };

  const onExit = () => {
    if (!linkSessionState.current) {
      console.error(
        '[Pinwheel Model Error]: Pinwheel session exiting with invalid session state.',
      );
      return;
    }

    const sessionState = linkSessionState.current;
    onOutcome(sessionState);
    setIsVisible(false);
  };

  return {
    isVisible,
    onEvent,
    onExit,
  };
};

export default usePinwheelEventsHandling;
